import { NgFor, NgIf } from '@angular/common';
import { Component, Input } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Geolocation } from '@capacitor/geolocation';
import { ILatLon } from '@curbnturf/entities';
import { waypointFormBuilder } from '@curbnturf/form-helpers/src/lib/forms/builder.helpers';
import { CoordinatesMapComponent } from '@curbnturf/map/src/lib/coordinates-map/coordinates-map.component';
import { MapModalComponent } from '@curbnturf/map/src/lib/map-modal/map-modal.component';
import { IonicModule, ModalController } from '@ionic/angular';
import { BehaviorSubject, Subject } from 'rxjs';

@Component({
  standalone: true,
  selector: 'curbnturf-add-waypoint',
  templateUrl: './add-waypoint.component.html',
  styleUrls: ['./add-waypoint.component.scss'],
  imports: [CoordinatesMapComponent, IonicModule, MapModalComponent, NgFor, NgIf, ReactiveFormsModule],
})
export class AddWaypointComponent {
  @Input() waypoints: FormArray;

  @Input() startingPoint: FormGroup;

  get pointList(): ILatLon[] {
    return [this.startingPoint?.value, ...(this.waypoints?.value || [])];
  }

  openModalId?: string;
  waypointModal: HTMLIonModalElement;
  pointListSubject: Subject<ILatLon[]>;

  constructor(
    private fb: FormBuilder,
    private modalController: ModalController,
  ) {
    this.pointListSubject = new BehaviorSubject(this.pointList);
  }

  async setCoordinatesToCurrent(waypoint: FormControl) {
    const location = await Geolocation.getCurrentPosition();
    this.setCoordinates(
      {
        lat: location.coords.latitude,
        lon: location.coords.longitude,
      },
      waypoint,
    );
  }

  async getCoordinatesFromMap(index: number) {
    const waypoint = this.waypoints?.at(index) as FormControl;

    // update the pointList subject as the subject is just for this
    this.pointListSubject.next(this.pointList);

    this.waypointModal = await this.modalController.create({
      component: MapModalComponent,
      componentProps: {
        pins$: this.pointListSubject.asObservable(),
        center: waypoint.value,
        draggable: false,
        text: 'Pick the next point on the trip',
        locationClicked: (coordinates: ILatLon) => this.locationClicked(waypoint, coordinates),
      },
    });
    await this.waypointModal.present();
  }

  locationClicked(waypoint: FormControl, coordinates: ILatLon) {
    this.setCoordinates(coordinates, waypoint);
  }

  addWaypoint() {
    this.waypoints.push(waypointFormBuilder(this.fb));
  }

  removeWaypoint(index: number): void {
    this.waypoints.removeAt(index);
  }

  moveUp(index: number): void {
    if (index === 0) {
      return;
    }
    const waypoint = this.waypoints.at(index);
    this.waypoints.removeAt(index);
    this.waypoints.insert(index - 1, waypoint);
    this.waypoints.markAsDirty();
  }

  moveDown(index: number): void {
    const waypoint = this.waypoints.at(index);
    this.waypoints.removeAt(index);
    this.waypoints.insert(index + 1, waypoint);
    this.waypoints.markAsDirty();
  }

  private setCoordinates(coordinates: ILatLon, waypoint: FormControl) {
    waypoint.patchValue(coordinates);
    this.waypoints.markAsDirty();

    this.pointListSubject.next(this.pointList);
  }
}
